import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { ref, push, set, onValue, off, serverTimestamp } from 'firebase/database';
import { auth, database } from '../config/firebase';
import { SurveyResponse } from '../types';
import {
  queueSubmission,
  getQueuedSubmissions,
  removeFromQueue,
  incrementAttempts,
  acquireLock,
  releaseLock,
} from '../utils/submissionQueue';

interface FirebaseContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  submitSurveyResponse: (response: Omit<SurveyResponse, 'id'>) => Promise<{ success: boolean; queued: boolean }>;
  getSurveyResults: (callback: (results: SurveyResponse[]) => void) => () => void;
  flushQueue: () => Promise<void>;
}

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

interface FirebaseProviderProps {
  children: React.ReactNode;
}

export function FirebaseProvider({ children }: FirebaseProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Listen to authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Attempt to flush the queue
  const flushQueue = useCallback(async () => {
    // Try to acquire lock - if another flush is in progress, skip
    if (!acquireLock()) {
      console.log('Queue flush already in progress, skipping');
      return;
    }

    try {
      const queue = getQueuedSubmissions();
      if (queue.length === 0) {
        releaseLock();
        return;
      }

      console.log(`Attempting to flush ${queue.length} queued submission(s)`);

      // Authenticate once before processing queue
      try {
        await signInAnonymously(auth);
      } catch (error) {
        console.error('Failed to authenticate for queue flush:', error);
        releaseLock();
        return;
      }

      for (const item of queue) {
        try {
          const resultsRef = ref(database, 'results');
          const newResultRef = push(resultsRef);

          const answersArray: string[] = [];
          item.response.answers.forEach(answer => {
            answersArray[answer.questionId] = String(answer.value);
          });

          const databaseResponse = {
            timestamp: serverTimestamp(),
            answers: answersArray,
            lang: item.response.language,
          };

          await set(newResultRef, databaseResponse);

          // Successfully sent - remove from queue
          removeFromQueue(item.id);
          console.log(`Flushed queued submission: ${item.id}`);
        } catch (error) {
          console.error(`Failed to flush submission ${item.id}:`, error);
          incrementAttempts(item.id);
        }
      }
    } finally {
      // Always release lock when done
      releaseLock();
    }
  }, []);

  // Anonymous authentication for data submission
  const authenticateAnonymously = async (): Promise<User> => {
    if (user) return user;

    try {
      const result = await signInAnonymously(auth);
      return result.user;
    } catch (error) {
      console.error('Authentication error:', error);
      throw new Error('Failed to authenticate');
    }
  };

  // Submit survey response to Realtime Database
  const submitSurveyResponse = useCallback(async (response: Omit<SurveyResponse, 'id'>): Promise<{ success: boolean; queued: boolean }> => {
    try {
      setError(null);

      // Ensure user is authenticated
      await authenticateAnonymously();

      // Create a new reference in the results collection
      const resultsRef = ref(database, 'results');
      const newResultRef = push(resultsRef);

      // Convert answers to old format: array where index = questionId, value = answer choice index
      const answersArray: string[] = [];
      response.answers.forEach(answer => {
        answersArray[answer.questionId] = String(answer.value);
      });

      // Prepare data for Realtime Database (matching previous structure)
      const databaseResponse = {
        timestamp: serverTimestamp(),
        answers: answersArray,
        lang: response.language,
      };

      // Save to Realtime Database
      await set(newResultRef, databaseResponse);

      console.log('Survey response submitted successfully');
      return { success: true, queued: false };
    } catch (error) {
      console.error('Error submitting survey response:', error);

      // Queue for later if submission failed
      queueSubmission(response as SurveyResponse);
      console.log('Survey response queued for later submission');

      return { success: false, queued: true };
    }
  }, [authenticateAnonymously]);

  // Real-time listener for survey results
  const getSurveyResults = useCallback((callback: (results: SurveyResponse[]) => void): (() => void) => {
    const resultsRef = ref(database, 'results');

    const unsubscribe = onValue(resultsRef, (snapshot) => {
      const data = snapshot.val();
      const results: SurveyResponse[] = [];

      if (data) {
        Object.entries(data).forEach(([key, value]: [string, any]) => {
          results.push({
            id: key,
            answers: value.answers,
            timestamp: new Date(value.timestamp || Date.now()),
            language: value.lang || value.language, // Support both 'lang' and 'language'
          });
        });

        // Sort by timestamp descending (most recent first)
        results.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      }

      callback(results);
    }, (error) => {
      console.error('Error fetching survey results:', error);
      setError('Failed to fetch survey results');
    });

    // Return cleanup function
    return () => off(resultsRef, 'value', unsubscribe);
  }, []);

  const value: FirebaseContextType = {
    user,
    loading,
    error,
    submitSurveyResponse,
    getSurveyResults,
    flushQueue,
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
}

export function useFirebase(): FirebaseContextType {
  const context = useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
}